
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calculator, Phone, Copy, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface QuoteCalculatorProps {
  formatCurrency: (amount: number) => string;
  formatPercentage: (percentage: number) => string;
}

const QuoteCalculator = ({ formatCurrency, formatPercentage }: QuoteCalculatorProps) => {
  const [askingPrice, setAskingPrice] = useState<number>(0);
  const [originalSalesPrice, setOriginalSalesPrice] = useState<number>(0);
  const [discountPercent, setDiscountPercent] = useState<number>(0.05);
  const [titleWork, setTitleWork] = useState<number>(150);
  const [appraisal, setAppraisal] = useState<number>(150);
  const [profitMargin, setProfitMargin] = useState<number>(3000);
  const [finalOffer, setFinalOffer] = useState<number>(0);
  const [netToCustomer, setNetToCustomer] = useState<number>(0);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (askingPrice > 0) {
      const discount = askingPrice * (discountPercent / 100);
      const afterDiscount = askingPrice - discount;
      const transferTax = originalSalesPrice * 0.0086;
      const totalDeductions = titleWork + appraisal + profitMargin;
      const calculatedOffer = afterDiscount - totalDeductions;
      const netAmount = calculatedOffer - transferTax;
      
      setFinalOffer(Math.max(0, calculatedOffer));
      setNetToCustomer(Math.max(0, netAmount));
    } else {
      setFinalOffer(0);
      setNetToCustomer(0);
    }
  }, [askingPrice, originalSalesPrice, discountPercent, titleWork, appraisal, profitMargin]);

  const handleCopyOffer = async () => {
    try {
      await navigator.clipboard.writeText(formatCurrency(netToCustomer));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: "Copied!",
        description: "Net amount to customer copied to clipboard",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  const resetCalculator = () => {
    setAskingPrice(0);
    setOriginalSalesPrice(0);
    setDiscountPercent(0.05);
    setTitleWork(150);
    setAppraisal(150);
    setProfitMargin(3000);
  };

  const discount = askingPrice * (discountPercent / 100);
  const afterDiscount = askingPrice - discount;
  const transferTax = originalSalesPrice * 0.0086;
  const totalDeductions = titleWork + appraisal + profitMargin;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <Calculator className="h-5 w-5 text-primary" />
              <span>Customer Call Quote Calculator</span>
            </CardTitle>
            <Button variant="outline" size="sm" onClick={resetCalculator}>
              Reset
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Input Section */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Asking Price</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={askingPrice || ''}
                  onChange={(e) => setAskingPrice(parseFloat(e.target.value) || 0)}
                  placeholder="Enter asking price"
                  className="text-lg"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Original Sales Price</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={originalSalesPrice || ''}
                  onChange={(e) => setOriginalSalesPrice(parseFloat(e.target.value) || 0)}
                  placeholder="Enter original sales price"
                  className="text-sm"
                />
                <p className="text-xs text-muted-foreground">Used to calculate transfer tax (0.86%)</p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Discount Percentage</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={discountPercent}
                  onChange={(e) => setDiscountPercent(parseFloat(e.target.value) || 0)}
                  placeholder="0.05"
                  className="text-sm"
                />
                <p className="text-xs text-muted-foreground">Default: 0.05% for title work</p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Title Work Cost</Label>
                <Input
                  type="number"
                  step="1"
                  value={titleWork}
                  onChange={(e) => setTitleWork(parseFloat(e.target.value) || 0)}
                  placeholder="150"
                  className="text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Appraisal Cost</Label>
                <Input
                  type="number"
                  step="1"
                  value={appraisal}
                  onChange={(e) => setAppraisal(parseFloat(e.target.value) || 0)}
                  placeholder="150"
                  className="text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Your Profit Margin</Label>
                <Input
                  type="number"
                  step="1"
                  value={profitMargin}
                  onChange={(e) => setProfitMargin(parseFloat(e.target.value) || 0)}
                  placeholder="3000"
                  className="text-sm"
                />
              </div>
            </div>

            {/* Calculation Breakdown */}
            <div className="space-y-4">
              <div className="bg-muted p-4 rounded-lg space-y-3">
                <h3 className="font-medium text-foreground">Calculation Breakdown</h3>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Starting Price:</span>
                    <span className="font-medium">{formatCurrency(askingPrice)}</span>
                  </div>
                  
                  <div className="flex justify-between text-destructive">
                    <span>Discount ({discountPercent}%):</span>
                    <span>-{formatCurrency(discount)}</span>
                  </div>
                  
                  <div className="flex justify-between border-t pt-2">
                    <span>After Discount:</span>
                    <span className="font-medium">{formatCurrency(afterDiscount)}</span>
                  </div>
                  
                  <div className="flex justify-between text-destructive">
                    <span>Title Work:</span>
                    <span>-{formatCurrency(titleWork)}</span>
                  </div>
                  
                  <div className="flex justify-between text-destructive">
                    <span>Appraisal:</span>
                    <span>-{formatCurrency(appraisal)}</span>
                  </div>
                  
                  <div className="flex justify-between text-destructive">
                    <span>Your Profit:</span>
                    <span>-{formatCurrency(profitMargin)}</span>
                  </div>
                  
                  <div className="border-t pt-2">
                    <div className="flex justify-between font-medium">
                      <span>Our Offer:</span>
                      <span>{formatCurrency(finalOffer)}</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between text-orange-600">
                    <span>Transfer Tax (0.86%):</span>
                    <span>-{formatCurrency(transferTax)}</span>
                  </div>
                </div>
              </div>

              {/* Net Amount to Customer */}
              <div className="bg-green-50 border-2 border-green-200 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-green-800 font-medium">Net to Customer (After Transfer Tax):</span>
                  <Badge className="bg-green-600">
                    <Phone className="h-3 w-3 mr-1" />
                    Ready to Quote
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-green-900">
                    {formatCurrency(netToCustomer)}
                  </span>
                  <Button
                    onClick={handleCopyOffer}
                    size="sm"
                    variant="outline"
                    className="ml-2"
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4 mr-1" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
                {netToCustomer <= 0 && askingPrice > 0 && (
                  <p className="text-red-600 text-sm mt-2">
                    Warning: Net amount is negative or zero. Adjust your parameters.
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  This is what the customer will receive after paying transfer tax at closing.
                </p>
              </div>

              {/* Quick Stats */}
              {askingPrice > 0 && netToCustomer > 0 && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Quick Stats</h4>
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between">
                      <span>Our Offer vs Asking:</span>
                      <span className="font-medium">
                        {formatPercentage((finalOffer / askingPrice) * 100)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Net vs Asking:</span>
                      <span className="font-medium text-green-600">
                        {formatPercentage((netToCustomer / askingPrice) * 100)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Transfer Tax:</span>
                      <span className="font-medium text-orange-600">
                        {formatCurrency(transferTax)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuoteCalculator;
